import { useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { SeoHead } from "@/components/public/SeoHead";
import { ShieldCheck, Loader2, AlertCircle } from "lucide-react";

const PRETTY_TYPE = {
  access: "data access",
  erasure: "data erasure",
  rectification: "data rectification",
  restriction: "processing restriction",
};

/**
 * Public landing page hit when a user clicks the confirmation URL the
 * admin sent them. Confirms email ownership and shows what happens next.
 */
export default function GdprConfirmPage() {
  const { token } = useParams();
  const [done, setDone] = useState(false);
  const [confirmedType, setConfirmedType] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  const confirmMut = useMutation({
    mutationFn: async () => (await api.post(`/public/gdpr-request/confirm/${token}`)).data,
    onSuccess: (r) => {
      setDone(true);
      setConfirmedType(r.request_type);
    },
    onError: (err) => {
      setErrorMsg(err?.response?.data?.detail || "Confirmation link is invalid or has already been used.");
    },
  });

  // Auto-fire confirmation on mount — the link itself is the proof of ownership
  useEffect(() => {
    if (token) confirmMut.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <SeoHead title="Confirm GDPR request — Sustainably Curated" description="Confirm email ownership for your data privacy request." path={`/gdpr/confirm/${token}`} />

      {confirmMut.isPending && (
        <div className="text-center py-12" data-testid="gdpr-confirm-loading">
          <Loader2 className="w-8 h-8 animate-spin text-forest mx-auto mb-3" />
          <p className="text-sm text-muted_text">Confirming…</p>
        </div>
      )}

      {done && (
        <div className="border border-line bg-ivory p-8" data-testid="gdpr-confirm-success">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="w-6 h-6 text-forest" />
            <h1 className="font-serif text-2xl">Email ownership confirmed.</h1>
          </div>
          <p className="text-sm text-muted_text leading-relaxed">
            Thanks. Your <strong>{PRETTY_TYPE[confirmedType] || "data privacy"}</strong> request
            is now ready for review. An administrator will process it shortly and
            follow up by email. No further action is required from you.
          </p>
        </div>
      )}

      {errorMsg && (
        <div className="border border-clay bg-clay/5 p-8" data-testid="gdpr-confirm-error">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-6 h-6 text-clay" />
            <h1 className="font-serif text-2xl">We couldn't confirm this link.</h1>
          </div>
          <p className="text-sm text-muted_text leading-relaxed">{errorMsg}</p>
          <p className="text-xs text-muted_text mt-3">If you believe this is a mistake, please submit a new request on our <a href="/contact" className="underline hover:text-forest">contact page</a>.</p>
        </div>
      )}
    </div>
  );
}
